import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import PackingListService from "../../../service/ERP/PackingList.service";
import "./packing-list.css";

const pkService = PackingListService();
const PAGE_STYLE_ID = "packing-list-print-page-style";

/* จำนวนแถวสินค้าสูงสุดต่อ 1 หน้า A4
   (281mm - หัวจดหมาย ~24mm - กล่องข้อมูล ~62mm - หัวตาราง ~10mm
    - แถว PO/LOADING/TOTAL ~16mm - สรุป+ลายเซ็น ~52mm) / 5.2mm ต่อแถว */
const ITEMS_PER_PAGE = 18;

/* ผู้ส่งออก — ตรงกับหัวกระดาษในฟอร์มต้นฉบับ */
const EXPORTER = {
  company: "NINE STAR FOOD CO., LTD.",
  address: "54/5 Moo 4, Tambon Khaowua, Amphoe Thamai, Chanthaburi  22120 Thailand",
  tel: "Tel +66 (0)81 8373092  (0)85 3674408",
};

const num = (v) => Number(v || 0);
const money = (v, d = 2) =>
  num(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const int = (v) => num(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fdate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "");
const up = (v) => String(v ?? "").toUpperCase();

/* แบ่งรายการเป็นหน้า — หน้าสุดท้ายเหลือที่ให้แถว LOADING / TOTAL */
function buildPages(rows) {
  if (!rows.length) return [[]];
  const pages = [];
  for (let i = 0; i < rows.length; i += ITEMS_PER_PAGE) {
    pages.push(rows.slice(i, i + ITEMS_PER_PAGE));
  }
  return pages;
}

function PackingListPage({ header, rows, totals, isLastPage, pageIndex, totalPages }) {
  const consigneeLines = String(header.consignee_address || "")
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  const carrierLines = String(header.carrier || "")
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <section className="pkl-page">
      {/* ── หัวจดหมาย ── */}
      <header className="pkl-head">
        <img src={logo} alt="NSF logo" className="pkl-logo" />
        <div className="pkl-head-text">
          <p className="pkl-co-name">{EXPORTER.company}</p>
          <p className="pkl-co-addr">{EXPORTER.address}</p>
          <p className="pkl-co-addr">{EXPORTER.tel}</p>
          <p className="pkl-doc-title">
            PACKING LIST
            {totalPages > 1 && <span className="pkl-pageno"> ({pageIndex + 1}/{totalPages})</span>}
          </p>
        </div>
      </header>

      {/* ── กล่องข้อมูลด้านบน ── */}
      <table className="pkl-tbl pkl-info">
        <colgroup>
          <col style={{ width: "58%" }} />
          <col style={{ width: "42%" }} />
        </colgroup>
        <tbody>
          <tr>
            <td>
              <span className="pkl-label">TO : </span>
              {up(header.consignee_name)}
            </td>
            <td><span className="pkl-label">No. &amp; date of invoice</span></td>
          </tr>

          <tr>
            <td className="pkl-nb-top pkl-indent">{up(consigneeLines[0] || "")}</td>
            <td className="pkl-nb-top pkl-indent">
              {[up(header.inv_no), header.inv_date ? `DATE :  ${fdate(header.inv_date)}` : ""]
                .filter(Boolean)
                .join("  ")}
            </td>
          </tr>

          <tr>
            <td className="pkl-nb-top pkl-indent">{up(consigneeLines[1] || "")}</td>
            <td><span className="pkl-label">Term of Payment</span></td>
          </tr>

          <tr>
            <td className="pkl-nb-top pkl-indent">
              {header.eori_no ? `EORI # ${up(header.eori_no)}` : ""}
            </td>
            <td className="pkl-nb-top pkl-indent">{up(header.payment_term)}</td>
          </tr>

          <tr>
            <td><span className="pkl-label">CONSIGNEE &amp; NOTIFY PARTY :</span></td>
            <td className="pkl-nb-top" rowSpan={2} />
          </tr>

          <tr>
            <td className="pkl-nb-top pkl-indent">{up(header.notify_party)}</td>
          </tr>

          <tr>
            <td style={{ padding: 0 }}>
              <table className="pkl-tbl" style={{ border: "none" }}>
                <colgroup>
                  <col style={{ width: "50%" }} />
                  <col style={{ width: "50%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td style={{ borderLeft: "none", borderTop: "none" }}>
                      <span className="pkl-label">Place of receipt</span>
                    </td>
                    <td style={{ borderRight: "none", borderTop: "none" }}>
                      <span className="pkl-label">Final destination</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="pkl-nb-top pkl-indent" style={{ borderLeft: "none", borderBottom: "none" }}>
                      {up(header.place_of_receipt)}
                    </td>
                    <td className="pkl-nb-top pkl-indent" style={{ borderRight: "none", borderBottom: "none" }}>
                      {up(header.final_destination)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td><span className="pkl-label">Advising Bank : SWIFT : {up(header.bank_swift)}</span></td>
          </tr>

          <tr>
            <td style={{ padding: 0 }}>
              <table className="pkl-tbl" style={{ border: "none" }}>
                <colgroup>
                  <col style={{ width: "50%" }} />
                  <col style={{ width: "50%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td style={{ borderLeft: "none", borderTop: "none" }}>
                      <span className="pkl-label">Carrier</span>
                    </td>
                    <td style={{ borderRight: "none", borderTop: "none" }}>
                      <span className="pkl-label">Sailing on or about</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="pkl-nb-top pkl-indent pkl-nb-bottom" style={{ borderLeft: "none" }}>
                      {up(carrierLines[0] || "")}
                    </td>
                    <td className="pkl-nb-top pkl-indent pkl-nb-bottom" style={{ borderRight: "none" }}>
                      {header.etd ? `ETD ${fdate(header.etd)}` : ""}
                    </td>
                  </tr>
                  <tr>
                    <td className="pkl-nb-top pkl-indent" style={{ borderLeft: "none", borderBottom: "none" }}>
                      {up(carrierLines[1] || "")}
                    </td>
                    <td className="pkl-nb-top pkl-indent" style={{ borderRight: "none", borderBottom: "none" }}>
                      {header.eta ? `ETA  ${fdate(header.eta)}` : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td className="pkl-nb-top pkl-indent pkl-pre">
              {[
                header.bank_acc_name ? `ACC NAME : ${up(header.bank_acc_name)}` : "",
                header.bank_acc_no ? `ACC NO. ${up(header.bank_acc_no)}` : "",
                up(header.bank_branch),
                up(header.bank_name),
              ]
                .filter(Boolean)
                .join("\n")}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── ตารางสินค้า ── */}
      <table className="pkl-tbl pkl-items">
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "8.5%" }} />
          <col style={{ width: "8.5%" }} />
        </colgroup>
        <thead>
          <tr>
            <th rowSpan={2}>MARK</th>
            <th>QUANTITY</th>
            <th>PACKING</th>
            <th rowSpan={2}>ITEM</th>
            <th rowSpan={2}>DESCRIPTION OF PRODUCT</th>
            <th>PROD.DATE</th>
            <th>EXP. DATE</th>
            <th>NW</th>
            <th>GW</th>
          </tr>
          <tr>
            <th>CASE</th>
            <th>SIZE</th>
            <th>D/M/Y</th>
            <th>D/M/Y</th>
            <th>KG</th>
            <th>KG</th>
          </tr>
        </thead>
        <tbody>
          {/* บรรทัดหัวเรื่อง PO — แสดงเฉพาะหน้าแรก */}
          {pageIndex === 0 && header.customer_po ? (
            <tr className="pkl-po-row">
              <td />
              <td />
              <td />
              <td />
              <td className="is-center">PO {up(header.customer_po)}</td>
              <td />
              <td />
              <td />
              <td />
            </tr>
          ) : null}

          {rows.map((row) => (
            <tr key={row.key}>
              <td>{up(row.mark)}</td>
              <td className="is-center">{row.quantity_case ? int(row.quantity_case) : ""}</td>
              <td className="is-center">{up(row.packing_size)}</td>
              <td className="is-center">{row.seq}</td>
              <td>{up(row.pg_name)}</td>
              <td className="is-center">{fdate(row.production_date)}</td>
              <td className="is-center">{fdate(row.expiry_date)}</td>
              <td className="is-right">{row.net_weight ? money(row.net_weight) : ""}</td>
              <td className="is-right">{row.gross_weight ? money(row.gross_weight) : ""}</td>
            </tr>
          ))}

          {/* แถวว่างคั่นก่อน LOADING ให้เหมือนต้นฉบับ */}
          {isLastPage && (
            <>
              <tr className="pkl-blank">
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
              </tr>
              <tr className="pkl-loading-row">
                <td />
                <td />
                <td />
                <td />
                <td>
                  {header.loading_qty
                    ? `LOADING :   ${int(header.loading_qty)} ${up(header.loading_unit || "PALLETS")}`
                    : ""}
                </td>
                <td />
                <td />
                <td />
                <td />
              </tr>
              <tr className="pkl-blank">
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
              </tr>
              <tr className="pkl-total-row">
                <td>TOTAL</td>
                <td className="is-center">{int(totals.cases)}</td>
                <td />
                <td />
                <td />
                <td />
                <td />
                <td className="is-right">{money(totals.net)}</td>
                <td className="is-right">{money(totals.gross)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {/* ── สรุป + ลายเซ็น (หน้าสุดท้ายเท่านั้น) ── */}
      {isLastPage && (
        <>
          <div className="pkl-summary">
            <div />
            <div>
              <div className="pkl-summary-row">
                <span>COUNTRY OF ORIGIN :</span>
                <span />
                <span className="pkl-summary-value">{up(header.country_of_origin)}</span>
                <span />
              </div>
              <div className="pkl-summary-row">
                <span>NET WEIGHT</span>
                <span>:</span>
                <span className="pkl-summary-value">{money(header.net_weight ?? totals.net)}</span>
                <span>KG</span>
              </div>
              <div className="pkl-summary-row">
                <span>GROSS WEIGHT</span>
                <span>:</span>
                <span className="pkl-summary-value">{money(header.gross_weight ?? totals.gross)}</span>
                <span>KG</span>
              </div>
              {num(header.volume_cbm) > 0 && (
                <div className="pkl-summary-row">
                  <span>VOLUME</span>
                  <span>:</span>
                  <span className="pkl-summary-value">{money(header.volume_cbm)}</span>
                  <span>CBM</span>
                </div>
              )}
            </div>
          </div>

          <div className="pkl-sign">
            <p>&quot;CERTIFIED TRUE AND CORRECT&quot;</p>
            <p className="pkl-sign-name">{up(header.signer_name)}</p>
            <p className="pkl-sign-pos">{up(header.signer_position)}</p>
          </div>
        </>
      )}
    </section>
  );
}

function PackingListPrintPreview() {
  const { code } = useParams();

  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);

  const handlePrint = () => window.print();

  /* @page scope ตาม element ไม่ได้ และไฟล์ print อื่นถูกรวมมาในบันเดิลเดียวกัน
     จึงฉีดกฎขนาดกระดาษของใบนี้ตอนสั่งพิมพ์ ให้เป็นตัวท้ายสุดเสมอ แล้วถอดออกหลังพิมพ์ */
  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const apply = (v) => {
      htmlStyle.setProperty("overflow", v, "important");
      htmlStyle.setProperty("overflow-y", v, "important");
      bodyStyle.setProperty("overflow", v, "important");
      bodyStyle.setProperty("overflow-y", v, "important");
    };
    apply("auto");

    const injectPageRule = () => {
      let el = document.getElementById(PAGE_STYLE_ID);
      if (!el) {
        el = document.createElement("style");
        el.id = PAGE_STYLE_ID;
        el.media = "print";
        el.innerHTML = "@page { size: A4 portrait; margin: 0; }";
        document.head.appendChild(el);
      }
    };
    const removePageRule = () => {
      const el = document.getElementById(PAGE_STYLE_ID);
      if (el) el.remove();
    };

    const onBefore = () => { injectPageRule(); apply("visible"); };
    const onAfter = () => { removePageRule(); apply("auto"); };

    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
      removePageRule();
    };
  }, []);

  useEffect(() => {
    pkService
      .get(code)
      .then((res) => {
        const { header: h, detail: d } = res.data?.data || {};
        setHeader(h || {});
        setDetails(d || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "Request failed. Please try again.",
          onOk: () => window.close(),
        });
      });
  }, [code]);

  const rows = useMemo(
    () =>
      (details || []).map((d, i) => ({
        key: `${d.pk_code || "row"}-${d.seq ?? i}`,
        seq: d.seq ?? i + 1,
        mark: d.mark || "",
        quantity_case: num(d.quantity_case),
        packing_size: d.packing_size || "",
        pg_name: d.pg_name || "",
        production_date: d.production_date,
        expiry_date: d.expiry_date,
        net_weight: num(d.net_weight),
        gross_weight: num(d.gross_weight),
      })),
    [details]
  );

  const totals = useMemo(
    () => ({
      cases: rows.reduce((s, r) => s + r.quantity_case, 0),
      net: rows.reduce((s, r) => s + r.net_weight, 0),
      gross: rows.reduce((s, r) => s + r.gross_weight, 0),
    }),
    [rows]
  );

  const pages = buildPages(rows);

  return (
    <div className="pkl-screen" id="packing-list">
      <div className="pkl-toolbar">
        <Button
          className="bn-center bg-blue-400"
          onClick={handlePrint}
          icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
        >
          PRINT
        </Button>
      </div>

      <div className="pkl-preview">
        {pages.map((pageRows, pageIndex) => (
          <PackingListPage
            key={`pkl-page-${pageIndex}`}
            header={header}
            rows={pageRows}
            totals={totals}
            isLastPage={pageIndex === pages.length - 1}
            pageIndex={pageIndex}
            totalPages={pages.length}
          />
        ))}
      </div>
    </div>
  );
}

export default PackingListPrintPreview;
